import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MessageCircle, ArrowLeft } from "lucide-react";
import PackageShowcaseArt from "@/components/showcase/PackageShowcaseArt";
import { getShowcaseVariant } from "@/components/showcase/showcaseUtils";
import Reveal from "@/components/ui/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { BRAND } from "@/constants/assets";

/**
 * Reusable layout for a single service-lane sub-page.
 * Each lane provides its own data via /pages/services/data/*.js.
 */
export default function ServiceLanePage({ lane }) {
  const showcaseVariant = getShowcaseVariant(
    `${lane.slug} ${lane.title} ${lane.eyebrow} ${lane.headline2}`,
  );

  usePageMeta({
    title: `${lane.title} · ${lane.priceRange}`,
    description: lane.description,
    path: `/services/${lane.slug}`,
    image: lane.boardImg,
  });

  return (
    <div data-testid={`page-service-${lane.slug}`} className="pt-28 md:pt-36">
      <section className="px-6 md:px-12 max-w-[1440px] mx-auto">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-[#e03c31] mb-6"
          data-testid="back-to-services"
        >
          <ArrowLeft size={12} /> All Services
        </Link>

        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
          ✱ {lane.eyebrow}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <h1 className="h1-display text-white text-[8vw] md:text-[4.5vw] max-w-[16ch]">
              {lane.headline1} <span className="text-[#e03c31]">{lane.headline2}</span>
            </h1>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">
              Price range
            </p>
            <p className="font-cabinet text-2xl md:text-3xl font-bold text-white leading-tight">
              {lane.priceRange}
            </p>
            <p className="text-zinc-400 text-sm md:text-[15px] mt-3 leading-relaxed">{lane.lede}</p>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-16 md:mt-24">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
          ✱ Active offers
        </p>
        <h2 className="h2-section text-2xl md:text-4xl mb-8">{lane.title} - full price ladder.</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lane.offers.map((offer, index) => (
            <Reveal key={offer.name} delay={index * 50}>
              <div
                data-testid={`offer-${lane.slug}-${index + 1}`}
                className="bg-[#0a0a0c] border border-white/5 rounded-2xl p-5 h-full card-lift card-glow flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-cabinet text-base font-bold leading-snug">{offer.name}</h3>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 shrink-0 ml-3">
                    {offer.tag || "One-time"}
                  </span>
                </div>
                <p className="font-cabinet text-xl font-bold text-[#e03c31] mb-2">{offer.price}</p>
                <p className="text-[13px] text-zinc-400 leading-relaxed flex-1">{offer.blurb}</p>
                <a
                  href={`${BRAND.whatsapp}?text=Hi%20ThemeStudios%2C%20I%20want%20the%20${encodeURIComponent(offer.name)}%20service.`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`offer-cta-${lane.slug}-${index + 1}`}
                  className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white hover:text-[#e03c31]"
                >
                  Ask about this <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7 order-2 md:order-1">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-4">
            ✱ What's included
          </p>
          <h2 className="h2-section text-2xl md:text-4xl mb-6">
            Brief-first. <span className="outline-text">Delivered ready-to-ship.</span>
          </h2>
          <ul className="space-y-3">
            {lane.includes.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm md:text-[15px] text-zinc-300">
                <Check size={16} className="text-[#e03c31] mt-1 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-5 order-1 md:order-2">
          <div className="relative overflow-hidden rounded-[1.75rem] border border-white/5 bg-[#0a0a0c] card-lift aspect-[4/5] min-h-[420px]">
            <PackageShowcaseArt
              image={lane.boardImg}
              variant={showcaseVariant}
              layout="portrait"
              className="absolute inset-0"
            />
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#e03c31] font-bold">
                Live ThemeStudios board
              </p>
              <p className="text-sm text-zinc-200 mt-1">
                Real 2026 pricing layout for the {lane.title.toLowerCase()} lane.
              </p>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mt-2.5 text-center">Live ThemeStudios pricing board · 2026</p>
        </div>
      </section>

      {lane.faqs && lane.faqs.length > 0 && (
        <section className="px-6 md:px-12 max-w-[1100px] mx-auto mt-24 md:mt-32">
          <p className="text-xs uppercase tracking-[0.22em] text-[#e03c31] font-bold mb-5">
            ✱ FAQ
          </p>
          <h2 className="font-cabinet text-3xl md:text-5xl font-black tracking-tight mb-10">
            Common questions.
          </h2>
          <div className="space-y-3">
            {lane.faqs.map((faq, index) => (
              <details
                key={index}
                data-testid={`faq-${lane.slug}-${index + 1}`}
                className="group bg-[#0a0a0c] border border-white/5 rounded-2xl p-6 [&>summary]:cursor-pointer"
              >
                <summary className="font-cabinet text-lg md:text-xl font-bold list-none flex items-center justify-between">
                  <span>{faq.q}</span>
                  <span className="text-[#e03c31] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="text-zinc-400 text-sm leading-relaxed mt-3">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 max-w-[1440px] mx-auto mt-24 md:mt-32">
        <div className="rounded-[2.5rem] glass p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-cabinet text-3xl md:text-5xl font-black tracking-tight max-w-xl">
            {lane.ctaHeadline}
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noreferrer"
              className="btn-magnetic"
              data-testid={`lane-cta-whatsapp-${lane.slug}`}
            >
              <MessageCircle size={16} /> WhatsApp brief
            </a>
            <Link
              to="/contact"
              className="btn-magnetic btn-ghost"
              data-testid={`lane-cta-form-${lane.slug}`}
            >
              Open form <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
